import { LayerDefinition } from './types';

export const foliageDefinition: LayerDefinition = {
  id: 'foliage',
  name: 'Fall Foliage',
  category: 'core_met',
  categoryLabel: 'Seasonal Phenology',
  icon: '🍁',
  description: `The Fall Foliage forecast provides a comprehensive set of visual tools to help autumn travelers, hikers, and photographers plan their trips:

• PEAK DATE - Displays a continental map of the projected calendar date when peak autumn color first arrives at each location. Colors transition chronologically from early September 15 peaks (green), through yellow, gold, orange, red, and brown, to mid-November (grey). Toggle between Full Landscape and Treed Regions Only, or click any location to see its exact projected peak date.

• DETAILED TIMING / CALENDAR - Shows an interactive calendar providing an at-a-glance seasonal color progression for any selected location, alongside daily continental maps showing color evolution from green canopy to peak foliage. Toggle between Full Landscape and Treed Regions Only to inspect regional forests.

• VIBRANCY THIS SEASON - Displays anticipated differences in peak color brilliance and saturation compared to an average season. Evaluates cool crisp nights (which trap anthocyanin red pigments), bright sunny days (which power sugar production), summer drought scorch, and wind penalties.

• CANOPY COVER - Displays satellite-measured tree canopy density (USGS NLCD & NALCMS) to show where broadleaf deciduous and mixed hardwood forests are concentrated across North America.

• TREE SPECIES - Maps native forest distributions for key autumn tree families, including Scarlet Red Maples, Canary Gold Aspens & Birches, Bronze Oaks & Beeches, and Golden Larches/Tamaracks.

• HEAT ANOMALY - Shows summer mean temperature departures compared to 30-year climate normals. Excessive summer heat stress damages leaf chlorophyll and can cause early scorching or dull browning.

• MOISTURE - Shows summer root-zone soil moisture anomalies compared to normal (from NOAA CPC). Soil moisture plays a critical role in tree health: severe drought causes premature leaf drop and muted colors, whereas healthy summer moisture supports vibrant displays.

How the Forecast Works & Data Timeline:
Our biophysical phenology model continuously integrates seasonal growing conditions, real-time weather observations, and short-term numerical forecasts:
• Antecedent Inputs: Summer soil moisture and heat stress define baseline tree vitality and drought resilience.
• Observed Weather: Ground-truth daily minimum temperatures from NOAA URMA lock in chilling accumulation through September and October.
• 5-to-7 Day Forecasts: Short-term weather predictions from NOAA NDFD drive active chilling and color onset. Note that forecasts 5 to 7 days out are dynamic and subject to update as weather systems shift.
• Long-Range Horizon: Dates beyond the short-term forecast window are guided by 30-year PRISM climatological normals for long-range trip planning.`,
  scoreDescription: `• Above Normal / Enhanced Vibrancy: Plentiful sunny afternoons, crisp above-freezing nights (34°F to 45°F), and calm winds promote intense anthocyanin pigment production, rich saturation, and extended peak retention with minimal early leaf drop.
• Average / Typical Vibrancy: Expected baseline autumn coloration aligned with 30-year historical climate norms for the region and species mix.
• Below Normal / Reduced Vibrancy: Unseasonably warm nights (preventing sugar trapping), severe summer drought scorch, persistent cloudy rain, or premature killing freezes produce muted, dull, or brown-tinted foliage.`,
  sourceModels: [
    'NOAA URMA (Observed Daily Tmin)',
    'NOAA NDFD (5-7 Day Weather Forecast)',
    'PRISM (30-Yr Daily Climatology Normals)',
    'NOAA CPC (Soil Moisture Anomalies)',
    'NASA Daymet (Solar Irradiance Normals)',
    'USGS NLCD & Tree Species Atlas'
  ],
  outputModels: [
    'Peak Color Arrival by Date (foliage_peak_date.pmtiles)',
    'Peak Color Arrival - Treed Only (foliage_peak_date_treed.pmtiles)',
    'Daily Color Progression (foliage_{date}.pmtiles)',
    'Seasonal Vibrancy Departure (foliage_vibrancy.pmtiles)',
    'Deciduous Tree Canopy Density (foliage_canopy.pmtiles)',
    'Native Tree Species Distribution (foliage_species.pmtiles)',
    'Summer Temperature Anomaly (foliage_summer_temp_anomaly.pmtiles)',
    'Summer Soil Moisture Anomaly (foliage_soil_moisture_anomaly.pmtiles)'
  ],
  config: {
    defaultModel: 'seasonal'
  }
};

export default foliageDefinition;
